import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEnvelope,
    faEnvelopeOpen,
    faPaperPlane,
    faPlus,
    faSpinner,
    faTrash,
    faPaperclip,
    faCog,
    faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../../../shared/services/api';
import { useEmailAccounts } from '../hooks/useEmailAccounts';
import EmailAccountsManager from './EmailAccountsManager';

export default function Mail() {
    const [emails, setEmails] = useState([]);
    const [selectedEmail, setSelectedEmail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showCompose, setShowCompose] = useState(false);
    const [composeForm, setComposeForm] = useState({
        from: '',
        to: '',
        subject: '',
        text: ''
    });
    const [sending, setSending] = useState(false);
    const [currentMailbox, setCurrentMailbox] = useState('INBOX');
    const [viewMode, setViewMode] = useState('inbox'); // 'inbox' or 'sent'
    const [recentEmails, setRecentEmails] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [showAccountsManager, setShowAccountsManager] = useState(false);
    const [showAccountSelector, setShowAccountSelector] = useState(false);

    // Email accounts hook
    const { accounts, selectedAccount, setSelectedAccount, refreshAccounts } = useEmailAccounts();

    // Update current mailbox when selected account changes
    useEffect(() => {
        if (selectedAccount) {
            setCurrentMailbox(selectedAccount.folderName);
            // Set default from address when account changes
            setComposeForm(prev => ({ ...prev, from: selectedAccount.email }));
        }
    }, [selectedAccount]);

    useEffect(() => {
        fetchEmails();
    }, [currentMailbox, viewMode, selectedAccount]);

    // Handle Telegram BackButton for compose view
    useEffect(() => {
        if (showCompose && window.Telegram?.WebApp?.BackButton) {
            const handleBack = () => setShowCompose(false);
            window.Telegram.WebApp.BackButton.onClick(handleBack);
            window.Telegram.WebApp.BackButton.show();

            return () => {
                window.Telegram.WebApp.BackButton.offClick(handleBack);
                window.Telegram.WebApp.BackButton.hide();
            };
        }
    }, [showCompose]);

    // Handle Telegram BackButton for email detail view
    useEffect(() => {
        if (selectedEmail && window.Telegram?.WebApp?.BackButton) {
            const handleBack = () => setSelectedEmail(null);
            window.Telegram.WebApp.BackButton.onClick(handleBack);
            window.Telegram.WebApp.BackButton.show();

            return () => {
                window.Telegram.WebApp.BackButton.offClick(handleBack);
                window.Telegram.WebApp.BackButton.hide();
            };
        }
    }, [selectedEmail]);

    const fetchEmails = async () => {
        try {
            setLoading(true);

            if (viewMode === 'inbox') {
                // INBOX MODE: Fetch emails from the selected account's folder
                const mailbox = selectedAccount?.folderName || 'INBOX';
                const response = await api.get(`/api/mail/inbox?mailbox=${mailbox}&limit=50`);
                const allEmails = response.emails || [];

                // Extract recent email addresses for autocomplete
                const addresses = new Set();
                allEmails.forEach(email => {
                    const fromAddr = email.from?.address;
                    if (fromAddr && !fromAddr.includes('bobrhub.com') && !fromAddr.includes('vps-bb1ce258')) {
                        addresses.add(fromAddr);
                    }
                });
                setRecentEmails(Array.from(addresses).slice(0, 5));

                // Show all emails from this folder (Sieve already filtered them)
                setEmails(allEmails);
            } else {
                // SENT MODE: Fetch all emails from INBOX and filter by sender
                const response = await api.get(`/api/mail/inbox?mailbox=INBOX&limit=50`);
                const allEmails = response.emails || [];

                // Filter emails sent FROM the selected account
                const sentEmails = allEmails.filter(email => {
                    const fromAddress = email.from?.address || email.from?.text || '';
                    return fromAddress.includes(selectedAccount?.email || 'niko@bobrhub.com');
                });

                setEmails(sentEmails);
            }
        } catch (error) {
            console.error('Error fetching emails:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSendEmail = async (e) => {
        e.preventDefault();
        try {
            setSending(true);
            await api.post('/api/mail/send', composeForm);

            // Reset form and close compose
            setComposeForm({
                from: selectedAccount?.email || '',
                to: '',
                subject: '',
                text: ''
            });
            setShowCompose(false);

            // Refresh inbox
            await fetchEmails();
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Ошибка при отправке письма');
        } finally {
            setSending(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Неизвестно';
        const date = new Date(dateString);
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays === 0) {
            return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
        } else if (diffDays === 1) {
            return 'Вчера';
        } else if (diffDays < 7) {
            return `${diffDays} дн. назад`;
        } else {
            return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
        }
    };

    const truncateText = (text, maxLength) => {
        if (!text) return '';
        return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    const getInitials = (name, email) => {
        if (name && name !== email) {
            return name.charAt(0).toUpperCase();
        }
        if (email) {
            return email.charAt(0).toUpperCase();
        }
        return '?';
    };

    const getAvatarColor = (text) => {
        const colors = [
            'from-blue-400 to-blue-600',
            'from-purple-400 to-purple-600',
            'from-pink-400 to-pink-600',
            'from-green-400 to-green-600',
            'from-yellow-400 to-yellow-600',
            'from-red-400 to-red-600',
            'from-indigo-400 to-indigo-600',
            'from-teal-400 to-teal-600',
        ];
        const index = (text || '').charCodeAt(0) % colors.length;
        return colors[index];
    };

    // Compose Modal
    if (showCompose) {
        return (
            <div className="flex flex-col h-full bg-theme-bg-color">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                    <div className="w-12"></div>
                    <h2 className="text-lg font-semibold text-theme-text-color">Новое письмо</h2>
                    <button
                        onClick={handleSendEmail}
                        disabled={sending || !composeForm.to || !composeForm.subject}
                        className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                            sending || !composeForm.to || !composeForm.subject
                                ? 'bg-theme-hint-color/20 text-theme-hint-color'
                                : 'bg-theme-button-color text-theme-button-text-color shadow-lg'
                        }`}
                    >
                        {sending ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                        ) : (
                            <FontAwesomeIcon icon={faPaperPlane} />
                        )}
                    </button>
                </div>

                {/* Compose Form */}
                <form onSubmit={handleSendEmail} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto">
                        {/* From */}
                        <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                            <div className="flex items-center px-4 py-3">
                                <label className="text-theme-hint-color text-sm mr-3 w-16">От:</label>
                                <select
                                    value={composeForm.from}
                                    onChange={(e) => setComposeForm({ ...composeForm, from: e.target.value })}
                                    className="flex-1 bg-transparent text-theme-text-color outline-none"
                                    required
                                >
                                    {accounts.map(account => (
                                        <option key={account.id} value={account.email}>
                                            {account.name} ({account.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* To */}
                        <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color relative">
                            <div className="flex items-center px-4 py-3">
                                <label className="text-theme-hint-color text-sm mr-3 w-16">Кому:</label>
                                <input
                                    type="email"
                                    placeholder="email@example.com"
                                    value={composeForm.to}
                                    onChange={(e) => setComposeForm({ ...composeForm, to: e.target.value })}
                                    onFocus={() => setShowSuggestions(true)}
                                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    className="flex-1 bg-transparent text-theme-text-color placeholder-theme-hint-color outline-none"
                                    required
                                />
                            </div>
                            {/* Email suggestions */}
                            {showSuggestions && recentEmails.length > 0 && !composeForm.to && (
                                <div className="absolute top-full left-0 right-0 bg-theme-secondary-bg-color border border-theme-section-separator-color rounded-b-xl shadow-2xl z-10 mx-4">
                                    <div className="text-xs text-theme-hint-color px-3 py-2 border-b border-theme-section-separator-color">
                                        Недавние контакты
                                    </div>
                                    {recentEmails.map((email, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setComposeForm({ ...composeForm, to: email });
                                                setShowSuggestions(false);
                                            }}
                                            className="px-3 py-3 cursor-pointer hover:bg-theme-bg-color text-theme-text-color text-sm border-b border-theme-section-separator-color last:border-b-0 transition-colors"
                                        >
                                            {email}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Subject */}
                        <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                            <div className="flex items-center px-4 py-3">
                                <label className="text-theme-hint-color text-sm mr-3 w-16">Тема:</label>
                                <input
                                    type="text"
                                    placeholder="О чём письмо?"
                                    value={composeForm.subject}
                                    onChange={(e) => setComposeForm({ ...composeForm, subject: e.target.value })}
                                    className="flex-1 bg-transparent text-theme-text-color placeholder-theme-hint-color outline-none"
                                    required
                                />
                            </div>
                        </div>

                        {/* Message */}
                        <div className="p-4">
                            <textarea
                                placeholder="Напишите сообщение..."
                                value={composeForm.text}
                                onChange={(e) => setComposeForm({ ...composeForm, text: e.target.value })}
                                className="w-full min-h-[400px] bg-transparent text-theme-text-color placeholder-theme-hint-color outline-none resize-none leading-relaxed"
                                style={{ fontFamily: 'inherit' }}
                            />
                        </div>
                    </div>
                </form>
            </div>
        );
    }

    const handleDeleteEmail = async () => {
        if (!window.confirm('Переместить письмо в корзину?')) {
            return;
        }

        try {
            // Pass uid as query parameter
            await api.delete('/api/mail/delete', { uid: selectedEmail.uid });
            setSelectedEmail(null);
            await fetchEmails();
        } catch (error) {
            console.error('Error deleting email:', error);
            alert('Ошибка при удалении письма');
        }
    };

    // Email Detail View
    if (selectedEmail) {
        const senderName = selectedEmail.from?.name || selectedEmail.from?.address || 'Неизвестный отправитель';
        const senderEmail = selectedEmail.from?.address || '';

        return (
            <div className="flex flex-col h-full bg-theme-bg-color">
                {/* Header */}
                <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                    <div className="flex items-center justify-between px-4 py-3">
                        <div className="flex-1"></div>
                        <h2 className="text-base font-semibold text-theme-text-color">Письмо</h2>
                        <button
                            onClick={handleDeleteEmail}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-theme-destructive-text-color hover:bg-theme-destructive-text-color/10 transition-colors"
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>

                {/* Email Content */}
                <div className="flex-1 overflow-y-auto">
                    {/* Subject */}
                    <div className="px-5 pt-5 pb-3">
                        <h1 className="text-2xl font-bold text-theme-text-color leading-tight">
                            {selectedEmail.subject || '(Без темы)'}
                        </h1>
                    </div>

                    {/* Sender Info */}
                    <div className="px-5 pb-4">
                        <div className="flex items-start gap-3">
                            {/* Avatar */}
                            <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(senderEmail)} flex items-center justify-center text-white font-bold text-lg flex-shrink-0 shadow-md`}>
                                {getInitials(senderName, senderEmail)}
                            </div>

                            <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="font-semibold text-theme-text-color truncate">
                                            {senderName}
                                        </div>
                                        <div className="text-sm text-theme-hint-color truncate">
                                            {senderEmail}
                                        </div>
                                        {/* To */}
                                        {selectedEmail.to && (
                                            <div className="text-xs text-theme-hint-color mt-1">
                                                Кому: {Array.isArray(selectedEmail.to)
                                                    ? selectedEmail.to.map(t => t.address || t).join(', ')
                                                    : selectedEmail.to.address || selectedEmail.to}
                                            </div>
                                        )}
                                    </div>
                                    <div className="text-xs text-theme-hint-color whitespace-nowrap">
                                        {selectedEmail.date ? new Date(selectedEmail.date).toLocaleString('ru-RU', {
                                            day: 'numeric',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                        }) : ''}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="h-2 bg-theme-secondary-bg-color"></div>

                    {/* Body */}
                    <div className="px-5 py-6">
                        {selectedEmail.html ? (
                            <div
                                className="text-theme-text-color prose prose-sm max-w-none leading-relaxed"
                                dangerouslySetInnerHTML={{ __html: selectedEmail.html }}
                                style={{
                                    fontSize: '15px',
                                    lineHeight: '1.6'
                                }}
                            />
                        ) : (
                            <div className="text-theme-text-color whitespace-pre-wrap leading-relaxed" style={{
                                fontSize: '15px',
                                lineHeight: '1.6'
                            }}>
                                {selectedEmail.text || 'Пустое сообщение'}
                            </div>
                        )}
                    </div>

                    {/* Attachments */}
                    {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                        <div className="px-5 pb-6">
                            <div className="text-sm font-semibold text-theme-text-color mb-3">
                                Вложения ({selectedEmail.attachments.length})
                            </div>
                            <div className="space-y-2">
                                {selectedEmail.attachments.map((attachment, index) => (
                                    <div
                                        key={index}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-theme-secondary-bg-color border border-theme-section-separator-color"
                                    >
                                        <div className="w-10 h-10 rounded-lg bg-theme-button-color/10 flex items-center justify-center text-theme-button-color flex-shrink-0">
                                            <FontAwesomeIcon icon={faPaperclip} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-theme-text-color font-medium truncate">
                                                {attachment.filename}
                                            </div>
                                            <div className="text-xs text-theme-hint-color">
                                                {attachment.size ? `${Math.round(attachment.size / 1024)} KB` : 'Неизвестный размер'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // Show accounts manager
    if (showAccountsManager) {
        return <EmailAccountsManager onClose={() => {
            setShowAccountsManager(false);
            refreshAccounts();
        }} />;
    }

    // Inbox List View
    return (
        <div className="flex flex-col h-full bg-theme-bg-color">
            {/* Header */}
            <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                <div className="flex items-center justify-between px-5 py-4">
                    <h1 className="text-3xl font-bold text-theme-text-color">Почта</h1>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setShowAccountsManager(true)}
                            className="w-12 h-12 rounded-full bg-theme-hint-color/20 text-theme-hint-color flex items-center justify-center hover:scale-105 transition-transform"
                        >
                            <FontAwesomeIcon icon={faCog} />
                        </button>
                        <button
                            onClick={() => {
                                // Initialize from field with current selected account
                                setComposeForm({
                                    from: selectedAccount?.email || '',
                                    to: '',
                                    subject: '',
                                    text: ''
                                });
                                setShowCompose(true);
                            }}
                            className="w-12 h-12 rounded-full bg-gradient-to-br from-theme-button-color to-theme-button-color shadow-lg text-theme-button-text-color flex items-center justify-center hover:scale-105 transition-transform"
                        >
                            <FontAwesomeIcon icon={faPlus} size="lg" />
                        </button>
                    </div>
                </div>

                {/* Account Selector */}
                {selectedAccount && (
                    <div className="px-5 pb-3">
                        <div className="relative">
                            <button
                                onClick={() => setShowAccountSelector(!showAccountSelector)}
                                className="w-full flex items-center justify-between px-4 py-3 bg-theme-bg-color rounded-xl border border-theme-section-separator-color hover:border-theme-button-color transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold`}>
                                        {selectedAccount.name.charAt(0)}
                                    </div>
                                    <div className="text-left">
                                        <div className="text-theme-text-color font-medium">{selectedAccount.name}</div>
                                        <div className="text-theme-hint-color text-sm">{selectedAccount.email}</div>
                                    </div>
                                </div>
                                <FontAwesomeIcon icon={faChevronDown} className={`text-theme-hint-color transition-transform ${showAccountSelector ? 'rotate-180' : ''}`} />
                            </button>

                            {showAccountSelector && (
                                <div className="absolute top-full left-0 right-0 mt-2 bg-theme-secondary-bg-color border border-theme-section-separator-color rounded-xl shadow-2xl z-10 overflow-hidden">
                                    {accounts.map(account => (
                                        <button
                                            key={account.id}
                                            onClick={() => {
                                                setSelectedAccount(account);
                                                setShowAccountSelector(false);
                                            }}
                                            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-theme-bg-color transition-colors border-b border-theme-section-separator-color last:border-b-0 ${
                                                account.id === selectedAccount.id ? 'bg-theme-bg-color' : ''
                                            }`}
                                        >
                                            <div className={`w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-semibold`}>
                                                {account.name.charAt(0)}
                                            </div>
                                            <div className="text-left flex-1">
                                                <div className="text-theme-text-color font-medium">{account.name}</div>
                                                <div className="text-theme-hint-color text-sm">{account.email}</div>
                                            </div>
                                            {account.id === selectedAccount.id && (
                                                <div className="w-2 h-2 rounded-full bg-theme-button-color"></div>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Mailbox Tabs */}
                <div className="flex gap-2 px-5 pb-4">
                    <button
                        onClick={() => setViewMode('inbox')}
                        className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-all ${
                            viewMode === 'inbox'
                                ? 'bg-theme-button-color text-theme-button-text-color shadow-md'
                                : 'text-theme-hint-color hover:bg-theme-bg-color'
                        }`}
                    >
                        Входящие
                    </button>
                    <button
                        onClick={() => setViewMode('sent')}
                        className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-all ${
                            viewMode === 'sent'
                                ? 'bg-theme-button-color text-theme-button-text-color shadow-md'
                                : 'text-theme-hint-color hover:bg-theme-bg-color'
                        }`}
                    >
                        Отправленные
                    </button>
                </div>
            </div>

            {/* Email List */}
            <div className="flex-1 overflow-y-auto">
                {loading ? (
                    <div className="flex items-center justify-center h-full">
                        <div className="text-center">
                            <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-theme-button-color mb-3" />
                            <p className="text-theme-hint-color text-sm">Загрузка писем...</p>
                        </div>
                    </div>
                ) : emails.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-theme-hint-color px-8">
                        <div className="w-24 h-24 rounded-full bg-theme-secondary-bg-color flex items-center justify-center mb-4">
                            <FontAwesomeIcon icon={faEnvelope} size="3x" className="opacity-40" />
                        </div>
                        <p className="text-lg font-medium">Нет писем</p>
                        <p className="text-sm mt-1 text-center">
                            {currentMailbox === 'INBOX' ? 'Входящие письма появятся здесь' : 'Отправленные письма появятся здесь'}
                        </p>
                    </div>
                ) : (
                    <div className="px-3 py-2 space-y-2">
                        {emails.map((email) => {
                            const senderName = email.from?.name || email.from?.address || 'Неизвестный отправитель';
                            const senderEmail = email.from?.address || '';

                            return (
                                <div
                                    key={email.uid || email.id}
                                    onClick={async () => {
                                        setSelectedEmail(email);
                                        // Mark as read if not already seen
                                        if (!email.seen && email.uid) {
                                            try {
                                                await api.post('/api/mail/mark-read', { uid: email.uid });
                                                // Update local state
                                                email.seen = true;
                                            } catch (error) {
                                                console.error('Error marking email as read:', error);
                                            }
                                        }
                                    }}
                                    className={`rounded-2xl p-4 cursor-pointer transition-all hover:shadow-md ${
                                        email.seen
                                            ? 'bg-theme-secondary-bg-color'
                                            : 'bg-gradient-to-r from-theme-button-color/5 to-transparent border-l-4 border-theme-button-color'
                                    }`}
                                >
                                    <div className="flex items-start gap-3">
                                        {/* Avatar */}
                                        <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(senderEmail)} flex items-center justify-center text-white font-bold flex-shrink-0 shadow-sm`}>
                                            {getInitials(senderName, senderEmail)}
                                        </div>

                                        {/* Content */}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-baseline justify-between gap-2 mb-1">
                                                <span className={`font-semibold truncate ${
                                                    email.seen ? 'text-theme-text-color' : 'text-theme-text-color'
                                                }`}>
                                                    {senderName}
                                                </span>
                                                <span className="text-xs text-theme-hint-color whitespace-nowrap flex items-center gap-1">
                                                    {!email.seen && (
                                                        <span className="w-2 h-2 rounded-full bg-theme-button-color"></span>
                                                    )}
                                                    {formatDate(email.date)}
                                                </span>
                                            </div>
                                            <div className={`text-sm mb-1.5 truncate ${
                                                email.seen ? 'text-theme-text-color' : 'font-semibold text-theme-text-color'
                                            }`}>
                                                {email.subject || '(Без темы)'}
                                            </div>
                                            <div className="text-sm text-theme-hint-color truncate leading-relaxed">
                                                {truncateText(email.text, 80)}
                                            </div>
                                            <div className="flex items-center gap-3 mt-2">
                                                {email.attachments && email.attachments.length > 0 && (
                                                    <div className="inline-flex items-center gap-1 text-xs text-theme-hint-color bg-theme-bg-color px-2 py-1 rounded-full">
                                                        <FontAwesomeIcon icon={faPaperclip} />
                                                        <span>{email.attachments.length}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Refresh Button (floating) */}
            {!loading && emails.length > 0 && (
                <button
                    onClick={fetchEmails}
                    className="fixed bottom-24 right-5 w-14 h-14 rounded-full bg-gradient-to-br from-theme-button-color to-theme-button-color text-theme-button-text-color shadow-2xl flex items-center justify-center z-10 hover:scale-110 transition-transform"
                >
                    <FontAwesomeIcon icon={faEnvelope} size="lg" />
                </button>
            )}
        </div>
    );
}
